import { Component, Inject, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BulkActionInstanceStatusInterface } from '@vendasta/bulk-actions';
import { Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, publishReplay, refCount, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { BulkActionsProgressReporter } from '../../bulk-actions-reporter.service';
import { HistoryItemsProgressReporter } from '../../history-items-reporter.service';
import { ProgressReporter } from '../../progress-reporter.service';
import { Progress, ProgressInputs, ProgressSubject } from './progress.subject';

@Component({
  selector: 'app-status-column',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
})
export class StatusComponent {
  readonly progressText$: Observable<string>;
  readonly progressPercent$: Observable<number>;
  readonly progressMode$: Observable<string>;
  readonly progressTooltip$: Observable<string>;
  private readonly historyItemId$$ = new ReplaySubject<string>(1);
  private readonly isLegacy$$ = new ReplaySubject<boolean>(1);

  constructor(
    @Inject(BulkActionsProgressReporter)
    private readonly bulkActionsReporter: ProgressReporter,
    @Inject(HistoryItemsProgressReporter)
    private readonly historyItemsReporter: ProgressReporter,
    private readonly translation: TranslateService,
  ) {
    const progress$ = this.buildProgress$();
    this.progressText$ = buildProgressText$(progress$, this.translation);
    this.progressTooltip$ = this.buildTooltipText$(progress$);
    this.progressMode$ = buildProgressBarMode$(progress$);
    this.progressPercent$ = buildProgressBarValue$(progress$);
  }

  @Input() set historyItemId(i: string) {
    this.historyItemId$$.next(i);
  }

  @Input() set workType(i: string) {
    const workThatUsesLegacySystem = ['AddFilteredAccountsHistoryItemModel', 'ImportListHistoryItemModel'];
    this.isLegacy$$.next(workThatUsesLegacySystem.includes(i));
  }

  private buildProgress$(): Observable<Progress> {
    return this.isLegacy$$.pipe(
      switchMap((isImport) => {
        if (isImport) {
          return this.buildProgressSubject(this.historyItemsReporter).asObservable();
        } else {
          return this.buildProgressSubject(this.bulkActionsReporter).asObservable();
        }
      }),
      shareReplay(1),
    );
  }

  private buildProgressSubject(reporter: ProgressReporter): ProgressSubject {
    const status$ = this.historyItemId$$.pipe(
      switchMap((id) => reporter.getPolledStatus$(id)),
      publishReplay(1),
      refCount(), // TODO: Is this needed?
    );
    const items$ = status$.pipe(
      map<BulkActionInstanceStatusInterface, ProgressInputs>((v) => ({
        processed: v.processedItems,
        expected: v.totalItems,
        isFinalized: v.isFinalized,
      })),
    );
    return new ProgressSubject(items$);
  }

  private buildTooltipText$(progress$: Observable<Progress>): Observable<string> {
    const key = 'BULK_ACTIONS.HISTORY.TOOLTIPS.STATUS_LOAD_FAILED';
    return progress$.pipe(
      filter((p) => p.status === 'failed_to_acquire'),
      switchMap(() => this.translation.stream(key)),
    );
  }
}

function buildProgressBarMode$(progress$: Observable<Progress>): Observable<string> {
  return progress$.pipe(
    map((p) => p.status),
    map((status) => {
      switch (status) {
        case 'acquiring':
          // TODO: Use indeterminate:
          //  There is a rendering glitch that currently happens with the
          //  indeterminate animation.  Presumably because the actionlists
          //  history component is creating too many objects:
          //  https://github.com/angular/components/issues/9050
          return 'indeterminate';
        case 'partial':
          return 'determinate';
        default:
          return 'determinate';
      }
    }),
  );
}

function buildProgressBarValue$(progress$: Observable<Progress>): Observable<number> {
  return progress$.pipe(
    map((p) => {
      if (p.expectedItems === 0) {
        return 100;
      }
      return (100 * p.processedItems) / p.expectedItems;
    }),
    startWith(0.5),
    // Mat-progress-bar will kill the whole page if it sees NaN.
    // This keeps the page functional, even if there is a bug.
    map((v) => (Number.isNaN(v) ? 0.5 : v)),
  );
}

export function buildProgressText$(progress$: Observable<Progress>, translator: TranslateService): Observable<string> {
  return progress$.pipe(
    switchMap((p) => {
      if (p.status === 'failed_to_acquire') {
        return translator.stream('BULK_ACTIONS.HISTORY.PROGRESS_BAR.UNKNOWN');
      }
      if (p.status === 'acquiring') {
        return translator.stream('BULK_ACTIONS.HISTORY.PROGRESS_BAR.LOADING');
      }
      const expected = p.expectedItems > 0 ? `${p.expectedItems}` : '?';
      if (p.processedItems <= 0) {
        // TODO: Surface more information during the initialization phase
        return translator.stream('BULK_ACTIONS.HISTORY.PROGRESS_BAR.LOADING');
      }
      if (p.processedItems === p.expectedItems && !p.isFinalized) {
        return translator.stream('BULK_ACTIONS.HISTORY.PROGRESS_BAR.FINALIZING');
      }
      return of<string>(`${p.processedItems} / ${expected}`);
    }),
    map((v) => `${v}`), // Overcome [stream]'s ambiguous type
  );
}
